import * as THREE from 'three';
import { PLAYER_H, PLAYER_HALF_W } from '../sim/constants.ts';

/**
 * The seam between the game and the Axie.
 *
 * The renderer draws whatever satisfies this interface and knows nothing about
 * where the model came from. Two implementations exist:
 *
 *   PlaceholderAxie - authored primitives, zero dependencies, always available.
 *   MixerAxie       - the real Axie from the Three.js Axie Mixer 3D toolkit.
 *
 * The seam is not defensive plumbing. `RIGHTS.md` grants Axie asset use only
 * inside an approved Vibeathon project, and the toolkit is a public alpha with
 * no promise of continued availability (brief risk R-13). A game that cannot
 * draw its own protagonist without a 513 MB asset pack present is a game that
 * cannot be developed, demoed, or tested offline. The placeholder keeps the
 * project buildable and playable on its own.
 */
export interface AxieCharacter {
  /** Added to the scene by the renderer. Origin is at the character's feet. */
  readonly object3D: THREE.Object3D;
  /** Advance animation. Called once per rendered frame, not per simulation tick. */
  update(deltaSeconds: number): void;
  /** Face left (-1) or right (+1). */
  setFacing(facing: 1 | -1): void;
  /** Whether the player is grounded and actually covering ground this frame -
   *  the simulation owns movement (brief §8), this only picks which clip to
   *  show for it. */
  setMoving(isMoving: boolean): void;
  /** Play the model's one-shot normal attack, if it has one. */
  playAttack(): void;
  dispose(): void;
}

/** Authored stand-in. Deliberately readable rather than pretty. */
export class PlaceholderAxie implements AxieCharacter {
  readonly object3D: THREE.Group;
  private readonly disposables: Array<{ dispose(): void }> = [];

  constructor(color = 0x8be9fd) {
    this.object3D = new THREE.Group();
    const geo = new THREE.CapsuleGeometry(PLAYER_HALF_W * 0.9, PLAYER_H * 0.5, 4, 8);
    const mat = new THREE.MeshLambertMaterial({ color });
    const mesh = new THREE.Mesh(geo, mat);
    // Capsule is centred on its origin; lift it so the group's origin is the feet.
    mesh.position.y = PLAYER_H / 2;
    this.object3D.add(mesh);
    this.disposables.push(geo, mat);
  }

  update(): void {
    /* static */
  }

  setFacing(facing: 1 | -1): void {
    this.object3D.scale.x = facing;
  }

  setMoving(): void {
    /* no clips to switch between */
  }

  playAttack(): void {
    /* no authored attack clip */
  }

  dispose(): void {
    for (const d of this.disposables) d.dispose();
    this.disposables.length = 0;
    this.object3D.clear();
  }
}

export interface MixerAxieOptions {
  /** Where `axie-mixer-copy-assets` put the content pack. */
  assetBaseUrl?: string;
  /** Axie genes. Round 1 uses a curated local roster, so no API key is needed. */
  genes: string;
  renderer: THREE.WebGLRenderer;
  /** World height in tiles to scale the model to. */
  targetHeight?: number;
  signal?: AbortSignal;
}

/**
 * Minimal runtime shape of the toolkit, declared locally.
 *
 * The package is resolved through a runtime dynamic import so this project still
 * typechecks and builds when it is absent - which it is by default, because it
 * is installed from a pinned Git revision rather than npm and its content pack
 * is not committed here. See README "Axie assets".
 */
interface MixerModule {
  createAxieMixer3D(options: {
    renderer?: unknown;
    assetBaseUrl?: string;
    signal?: AbortSignal;
  }): Promise<MixerHandle>;
}

interface MixerHandle {
  createFromGenes(request: {
    genes: string;
    signal?: AbortSignal;
    extensions?: { quality?: string; artMode?: string; strict?: boolean };
  }): Promise<AxieHandle>;
  dispose(): void;
}

interface AxieHandle {
  wrapper: THREE.Object3D;
  update(deltaSeconds: number): void;
  setMoveSpeed?(speed: number): void;
  dispose(): void;
}

const MIXER_MODULE_ID = '@jaatster/threejs-axie-mixer3d-public';

/** The real Axie. Construct with `MixerAxie.create`; the constructor is private
 *  because building one requires two awaits. */
export class MixerAxie implements AxieCharacter {
  readonly object3D: THREE.Group;

  private constructor(
    private readonly mixer: MixerHandle,
    private readonly axie: AxieHandle,
    scale: number,
  ) {
    this.object3D = new THREE.Group();
    this.object3D.add(axie.wrapper);
    this.object3D.scale.setScalar(scale);
  }

  /**
   * Returns null rather than throwing when the toolkit or its assets are not
   * present, so the caller falls back to the placeholder.
   *
   * Deliberately lazy: this is a dynamic import so the toolkit and its manifest
   * (8.6 MB on its own) never load on the path to Room 1, which has a
   * sub-5-second cold-load budget (brief §15, risk R-03).
   */
  static async create(options: MixerAxieOptions): Promise<MixerAxie | null> {
    const { genes, renderer, assetBaseUrl = '/assets/axie/', targetHeight = PLAYER_H } = options;
    try {
      // The dynamic import's specifier is not statically known (see the
      // @vite-ignore below), so TypeScript can only ever see it as `any` -
      // an explicit cast here, not a declared-type annotation, is what tells
      // the linter this is a deliberate, reviewed assertion rather than an
      // `any` silently leaking into a typed variable.
      const mod = (await import(/* @vite-ignore */ MIXER_MODULE_ID)) as MixerModule;
      const mixer = await mod.createAxieMixer3D({
        renderer,
        assetBaseUrl,
        ...(options.signal ? { signal: options.signal } : {}),
      });
      const axie = await mixer.createFromGenes({
        genes,
        ...(options.signal ? { signal: options.signal } : {}),
        // `quality`/`artMode`/`strict` are deprecated as top-level fields in the
        // toolkit's current types even though its README still shows them there.
        extensions: { quality: 'balanced', artMode: 'faithful', strict: true },
      });
      // The game is a puzzle-platformer, not a walk cycle: the simulation owns
      // position, so the model must not self-propel.
      axie.setMoveSpeed?.(0);

      const box = new THREE.Box3().setFromObject(axie.wrapper);
      const height = box.max.y - box.min.y;
      const scale = height > 0 ? targetHeight / height : 1;
      return new MixerAxie(mixer, axie, scale);
    } catch (err) {
      console.warn(
        `[moonseed] Axie Mixer unavailable, using placeholder. ${(err as Error).message}`,
      );
      return null;
    }
  }

  update(deltaSeconds: number): void {
    this.axie.update(deltaSeconds);
  }

  setFacing(facing: 1 | -1): void {
    this.object3D.scale.x = Math.abs(this.object3D.scale.x) * facing;
  }

  /**
   * Left as a no-op rather than guessed at.
   *
   * `create()` already zeroes `setMoveSpeed` permanently to stop the toolkit
   * self-propelling the wrapper (the simulation owns position). Whether that
   * same call also drives an idle/walk animation blend independent of actual
   * movement is undocumented and this path is currently unreachable in
   * Round 1 (`STARTER_AXIE_GENES` is empty) - wire this once the Round 2
   * gene roster lands and the toolkit's real behaviour can be verified
   * against a running mixer instead of assumed from its types.
   */
  setMoving(): void {
    /* not yet reachable in Round 1; see comment above */
  }

  playAttack(): void {
    /* Mixer attack bindings are not part of the Round 1 path. */
  }

  dispose(): void {
    this.object3D.clear();
    this.axie.dispose();
    this.mixer.dispose();
  }
}

/** Clip names present on every mascot in the asset catalog. */
export type AxieClip = 'Idle' | 'Walk' | 'Run' | 'Greeting' | 'Dead';

export interface GlbAxieOptions {
  url: string;
  /** World height in tiles to scale the model to. */
  targetHeight?: number;
  signal?: AbortSignal;
}

/**
 * A ready-made Axie loaded from a single self-contained GLB.
 *
 * This is the Round 1 character. Compared with the modular gene mixer it is
 * roughly 0.9 MB instead of a 513 MB content pack plus a 3 MB runtime chunk, it
 * needs no gene string, and it loads with the stock GLTFLoader — so it carries
 * no peer-version constraint of its own.
 *
 * What it gives up is the thing the mixer exists for: these are fixed
 * characters with no swappable part slots. The Horn and Back unlocks still work
 * as mechanics, because the simulation owns the verbs, but they do not change
 * how the Axie looks. Round 2 restores that by swapping this for MixerAxie
 * behind the same interface.
 */
export class GlbAxie implements AxieCharacter {
  readonly object3D: THREE.Group;
  private readonly mixer: THREE.AnimationMixer;
  private readonly clips = new Map<string, THREE.AnimationAction>();
  private current: THREE.AnimationAction | null = null;
  private moving = false;
  private attackRemaining = 0;
  private readonly textures: THREE.Texture[] = [];
  private readonly geometries: THREE.BufferGeometry[] = [];
  private readonly materials: THREE.Material[] = [];

  private constructor(root: THREE.Object3D, animations: THREE.AnimationClip[], scale: number) {
    this.object3D = new THREE.Group();
    // Inner group carries the model's own scale so setFacing can mirror the
    // outer group without fighting it.
    const inner = new THREE.Group();
    inner.add(root);
    inner.scale.setScalar(scale);
    this.object3D.add(inner);

    this.mixer = new THREE.AnimationMixer(root);
    for (const clip of animations) {
      this.clips.set(clip.name, this.mixer.clipAction(clip));
    }

    // Collect GPU resources up front; GLTFLoader gives no disposal helper and
    // the renderer leak test (brief §15) will catch anything missed here.
    root.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (!mesh.isMesh) return;
      if (mesh.geometry) this.geometries.push(mesh.geometry);
      for (const mat of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
        if (!mat) continue;
        this.materials.push(mat);
        // `.isTexture` rather than `instanceof`: three tags its classes with
        // boolean flags precisely so checks survive two copies of the library
        // being loaded, which an examples/jsm import can cause.
        for (const value of Object.values(mat)) {
          const tex = value as THREE.Texture | null;
          if (tex?.isTexture) this.textures.push(tex);
        }
      }
    });

    this.play('Idle');
  }

  static async create(options: GlbAxieOptions): Promise<GlbAxie | null> {
    const { url, targetHeight = PLAYER_H } = options;
    try {
      // Imported here rather than at module scope so GLTFLoader (~95 kB) stays
      // out of the initial bundle. Nothing on the path to Room 1 needs it.
      const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
      const loader = new GLTFLoader();
      const gltf = await loader.loadAsync(url);
      if (options.signal?.aborted) return null;

      const box = new THREE.Box3().setFromObject(gltf.scene);
      const height = box.max.y - box.min.y;
      const scale = height > 0 ? targetHeight / height : 1;
      // Drop the model so its feet sit on the group origin, matching the
      // simulation's convention that the player's y is its feet.
      gltf.scene.position.y = -box.min.y;

      return new GlbAxie(gltf.scene, gltf.animations, scale);
    } catch (err) {
      console.warn(
        `[moonseed] Axie GLB failed to load, using placeholder. ${(err as Error).message}`,
      );
      return null;
    }
  }

  /** Cross-fades to a clip. Unknown names are ignored so callers can ask for a
   *  clip a given mascot happens not to ship.
   *
   *  `string & {}` rather than plain `string`: a bare `AxieClip | string`
   *  collapses to `string` and loses autocomplete on the known clip names -
   *  this keeps them while still accepting anything. */
  play(name: AxieClip | (string & {}), fadeSeconds = 0.18): void {
    const next = this.clips.get(name);
    if (!next || next === this.current) return;
    next.reset().play();
    if (this.current) this.current.crossFadeTo(next, fadeSeconds, false);
    this.current = next;
  }

  update(deltaSeconds: number): void {
    this.mixer.update(deltaSeconds);
    if (this.attackRemaining <= 0) return;
    this.attackRemaining -= deltaSeconds;
    if (this.attackRemaining <= 0) this.play(this.moving ? 'Walk' : 'Idle');
  }

  setFacing(facing: 1 | -1): void {
    // The model faces camera-forward; yaw it rather than mirroring the scale,
    // which would flip its normals and light it inside-out.
    this.object3D.rotation.y = facing === 1 ? Math.PI * 0.25 : -Math.PI * 0.25;
  }

  setMoving(isMoving: boolean): void {
    this.moving = isMoving;
    if (this.attackRemaining <= 0) this.play(isMoving ? 'Walk' : 'Idle');
  }

  playAttack(): void {
    const attack = [...this.clips.entries()].find(([name]) => name.endsWith('.Attack'))?.[1];
    if (!attack) return;
    attack.reset();
    attack.timeScale = 3;
    attack.setLoop(THREE.LoopOnce, 1);
    attack.clampWhenFinished = true;
    attack.play();
    if (this.current && this.current !== attack) this.current.crossFadeTo(attack, 0.08, false);
    this.current = attack;
    this.attackRemaining = attack.getClip().duration / attack.timeScale;
  }

  dispose(): void {
    this.mixer.stopAllAction();
    this.mixer.uncacheRoot(this.mixer.getRoot());
    for (const t of this.textures) t.dispose();
    for (const g of this.geometries) g.dispose();
    for (const m of this.materials) m.dispose();
    this.textures.length = 0;
    this.geometries.length = 0;
    this.materials.length = 0;
    this.object3D.clear();
  }
}

/** The Round 1 Starter Axie. Kotaro is the lightest mascot in the catalog
 *  (0.92 MB, 47 bones) that still ships the full Idle/Walk/Run/Greeting/Dead
 *  clip set, which matters for the lowest-supported-device frame budget. */
export const STARTER_AXIE_URL = '/assets/characters/kotaro.glb';

export const PLAYABLE_CHARACTERS = [
  { id: 'kotaro', name: 'Kotaro', axieClass: 'Beast', url: STARTER_AXIE_URL },
  { id: 'kibo', name: 'Kibo', axieClass: 'Plant', url: '/assets/characters/kibo.glb' },
  { id: 'pomodoro', name: 'Pomodoro', axieClass: 'Bug', url: '/assets/characters/pomodoro.glb' },
] as const;

export type PlayableCharacter = (typeof PLAYABLE_CHARACTERS)[number];
export type PlayableCharacterId = PlayableCharacter['id'];

export function playableCharacterById(id: string): PlayableCharacter {
  return PLAYABLE_CHARACTERS.find((character) => character.id === id) ?? PLAYABLE_CHARACTERS[0];
}

/**
 * The Starter Axie: a Plant, matching the brief's Starter loadout (§8).
 *
 * EMPTY BY DESIGN. Populating this needs a gene string from the curated roster,
 * and open decision §18.8 has not settled which Axies that roster contains or
 * under whose authorization. Until then the game runs on the placeholder, which
 * is a legitimate shipping state, not a broken one.
 */
export const STARTER_AXIE_GENES = '';

/** Resolves to the real Axie when the toolkit, assets and genes are all present,
 *  and to the placeholder otherwise. */
export async function createCharacterAxie(
  character: PlayableCharacter,
  renderer: THREE.WebGLRenderer,
  signal?: AbortSignal,
): Promise<AxieCharacter> {
  // Round 2 path: a real gene-composed Axie, once the roster is settled (§18.8).
  if (STARTER_AXIE_GENES !== '') {
    const mixed = await MixerAxie.create({
      genes: STARTER_AXIE_GENES,
      renderer,
      ...(signal ? { signal } : {}),
    });
    if (mixed) return mixed;
  }

  // Round 1 path: a ready-made GLB mascot.
  const glb = await GlbAxie.create({
    url: character.url,
    ...(signal ? { signal } : {}),
  });
  if (glb) return glb;

  return new PlaceholderAxie();
}
